import type { Cell } from './types';

function distanceBetween(start: Cell, end: Cell): number {
  return Math.hypot(end.x - start.x, end.y - start.y);
}

function samePoint(left: Cell, right: Cell): boolean {
  return Math.abs(left.x - right.x) < 0.0001 && Math.abs(left.y - right.y) < 0.0001;
}

export function polylineLength(points: readonly Cell[]): number {
  return points.slice(1).reduce((total, point, index) => {
    const previous = points[index];
    return previous === undefined ? total : total + distanceBetween(previous, point);
  }, 0);
}

export function pointOnPolyline(points: readonly Cell[], distance: number): Cell | undefined {
  const first = points[0];
  if (first === undefined) {
    return undefined;
  }

  const totalLength = polylineLength(points);
  const target = Math.min(Math.max(distance, 0), totalLength);
  let travelled = 0;

  for (let index = 1; index < points.length; index += 1) {
    const start = points[index - 1];
    const end = points[index];
    if (start === undefined || end === undefined) {
      continue;
    }
    const segmentLength = distanceBetween(start, end);
    if (segmentLength === 0) {
      continue;
    }
    if (travelled + segmentLength >= target) {
      const ratio = (target - travelled) / segmentLength;
      return {
        x: start.x + (end.x - start.x) * ratio,
        y: start.y + (end.y - start.y) * ratio,
      };
    }
    travelled += segmentLength;
  }

  return points.at(-1);
}

export function slicePolyline(
  points: readonly Cell[],
  startDistance: number,
  endDistance: number,
): readonly Cell[] {
  const totalLength = polylineLength(points);
  const start = Math.min(Math.max(startDistance, 0), totalLength);
  const end = Math.min(Math.max(endDistance, start), totalLength);
  const startPoint = pointOnPolyline(points, start);
  const endPoint = pointOnPolyline(points, end);
  if (startPoint === undefined || endPoint === undefined) {
    return [];
  }

  const result: Cell[] = [startPoint];
  let travelled = 0;
  for (let index = 1; index < points.length; index += 1) {
    const segmentStart = points[index - 1];
    const segmentEnd = points[index];
    if (segmentStart === undefined || segmentEnd === undefined) {
      continue;
    }
    travelled += distanceBetween(segmentStart, segmentEnd);
    if (travelled > start && travelled < end && !samePoint(result.at(-1) ?? startPoint, segmentEnd)) {
      result.push(segmentEnd);
    }
  }

  if (!samePoint(result.at(-1) ?? startPoint, endPoint)) {
    result.push(endPoint);
  }
  return result;
}
